/**
 * Django REST API Client
 * Generic REST API client with JWT auth, auto-refresh, and snake_case ↔ camelCase conversion.
 *
 * Token refresh is synchronized across all concurrent requests to prevent race conditions.
 */

// ═══════════════════════════════════════════════════════════════
// Environment / URL helpers
// ═══════════════════════════════════════════════════════════════

function getRuntimeEnv(key: string): string | undefined {
  if (typeof window !== 'undefined') {
    const win = window as unknown as Record<string, unknown>
    const env = win.__ENV__ as Record<string, string> | undefined
    if (env?.[key] && !env[key].includes('${')) {return env[key]}
  }
  return undefined
}

function enforceHttps(url: string): string {
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && url.startsWith('http:')) {
    return url.replace(/^http:/, 'https:')
  }
  return url
}

const rawApiUrl = getRuntimeEnv('VITE_API_URL') || import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000/api/v1'
export const API_URL = enforceHttps(rawApiUrl)

const rawLandingUrl = getRuntimeEnv('VITE_LANDING_URL') || import.meta.env.VITE_LANDING_URL
export const LANDING_URL = rawLandingUrl
  ? enforceHttps(rawLandingUrl)
  : API_URL.replace(/\/api\/v1$/, '')

if (import.meta.env.DEV) {
  console.log('[API] Resolved API_URL:', API_URL)
  console.log('[API] Resolved LANDING_URL:', LANDING_URL)
}

// ═══════════════════════════════════════════════════════════════
// Single source of truth for auth tokens (localStorage for persistence)
//
// SECURITY NOTE: Storing JWT tokens in localStorage makes them vulnerable to
// XSS extraction. A future hardening step should move to httpOnly, SameSite
// secure cookies with a CSRF-safe refresh mechanism, which requires backend
// changes to support cookie-based auth.
// ═══════════════════════════════════════════════════════════════

const STORAGE_KEY_ACCESS = 'access_token'
const STORAGE_KEY_REFRESH = 'refresh_token'

function _storageGet(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function _storageSet(key: string, value: string | null): void {
  try {
    if (value) {localStorage.setItem(key, value)}
    else {localStorage.removeItem(key)}
  } catch { /* noop */ }
}

/** Auth store — single source of truth. All reads/writes go through here. */
export const authStore = {
  get token(): string | null {
    return _storageGet(STORAGE_KEY_ACCESS)
  },
  get refreshToken(): string | null {
    return _storageGet(STORAGE_KEY_REFRESH)
  },
  get isValid(): boolean {
    return !!this.token
  },
  save(token: string, refresh: string): void {
    _storageSet(STORAGE_KEY_ACCESS, token)
    _storageSet(STORAGE_KEY_REFRESH, refresh)
  },
  clear(): void {
    _storageSet(STORAGE_KEY_ACCESS, null)
    _storageSet(STORAGE_KEY_REFRESH, null)
  },
}

function redirectToLogin(): void {
  if (typeof window !== 'undefined') {
    window.location.href = '/'
  }
}

// ═══════════════════════════════════════════════════════════════
// Synchronized token refresh (prevents race conditions)
// ═══════════════════════════════════════════════════════════════

let _refreshPromise: Promise<string | null> | null = null

async function _doRefresh(): Promise<string | null> {
  const refresh = authStore.refreshToken
  if (!refresh) {
    authStore.clear()
    redirectToLogin()
    return null
  }

  try {
    const res = await fetch(`${API_URL}/auth/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
    })
    if (!res.ok) {
      authStore.clear()
      redirectToLogin()
      return null
    }
    const data = await res.json()
    authStore.save(data.access, refresh)
    return data.access
  } catch {
    authStore.clear()
    redirectToLogin()
    return null
  }
}

async function refreshAccessToken(): Promise<string | null> {
  if (_refreshPromise) {
    return _refreshPromise
  }
  _refreshPromise = _doRefresh().finally(() => {
    _refreshPromise = null
  })
  return _refreshPromise
}

// ═══════════════════════════════════════════════════════════════
// apiFetch with synchronized refresh
// ═══════════════════════════════════════════════════════════════

export async function apiFetch(
  path: string,
  options: RequestInit = {},
  retry = true
): Promise<Response> {
  const url =
    path.startsWith('http://') || path.startsWith('https://')
      ? path
      : `${API_URL}${path}`
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  }

  const token = authStore.token
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const res = await fetch(url, { ...options, headers })

  // Handle 401 by attempting token refresh once (synchronized)
  if (res.status === 401 && retry) {
    const newToken = await refreshAccessToken()
    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`
      return fetch(url, { ...options, headers })
    }
    return res
  }

  if (res.status === 401 && !authStore.refreshToken) {
    authStore.clear()
    redirectToLogin()
  }

  return res
}

// ═══════════════════════════════════════════════════════════════
// Snake-case ↔ camelCase helpers
// ═══════════════════════════════════════════════════════════════

function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

export function snakeToCamel<T>(obj: unknown): T {
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return obj as T
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => snakeToCamel(item)) as T
  }
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj)) {
    result[toCamelCase(key)] = snakeToCamel(value)
  }
  return result as T
}

export function camelToSnake(obj: unknown): unknown {
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return obj
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => camelToSnake(item))
  }
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj)) {
    result[toSnakeCase(key)] = camelToSnake(value)
  }
  return result
}

// ═══════════════════════════════════════════════════════════════
// Collection-style API
// ═══════════════════════════════════════════════════════════════

export interface ApiListResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

export function createCollection<T extends object>(name: string) {
  const basePath = `/${name}/`

  return {
    async getFullList(options?: { sort?: string; filter?: string }): Promise<T[]> {
      const params = new URLSearchParams()
      params.set('page_size', '1000')
      if (options?.sort) {params.set('ordering', options.sort)}

      // The backend caps page_size (currently 50), so a single request only
      // returns the first page. Follow the `next` cursor until exhausted to
      // guarantee the full list — deep links to records beyond page 1 break
      // otherwise (e.g. /classes/<id> showing "Session not found").
      const all: T[] = []
      let url: string | null = `${basePath}?${params.toString()}`
      while (url) {
        const res = await apiFetch(url)
        if (!res.ok) {throw new Error(`Failed to list ${name}`)}
        const data: ApiListResponse<Record<string, unknown>> = await res.json()
        all.push(...data.results.map((item) => snakeToCamel<T>(item)))
        url = data.next
      }
      return all
    },

    async getList(page = 1, perPage = 50, options?: { sort?: string }): Promise<ApiListResponse<T>> {
      const params = new URLSearchParams()
      params.set('page', String(page))
      params.set('page_size', String(perPage))
      if (options?.sort) {params.set('ordering', options.sort)}

      const res = await apiFetch(`${basePath}?${params.toString()}`)
      if (!res.ok) {throw new Error(`Failed to list ${name}`)}
      const raw = await res.json() as ApiListResponse<Record<string, unknown>>
      return {
        ...raw,
        results: raw.results.map((item) => snakeToCamel<T>(item)),
      }
    },

    async getOne(id: string): Promise<T> {
      const res = await apiFetch(`${basePath}${id}/`)
      if (!res.ok) {throw new Error(`Failed to get ${name}/${id}`)}
      return snakeToCamel<T>(await res.json())
    },

    async create(data: Record<string, unknown>): Promise<T> {
      const res = await apiFetch(basePath, {
        method: 'POST',
        body: JSON.stringify(data),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || `Failed to create ${name}`)
      }
      return snakeToCamel<T>(await res.json())
    },

    async update(id: string, data: Record<string, unknown>): Promise<T> {
      const res = await apiFetch(`${basePath}${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      })
      if (!res.ok) {throw new Error(`Failed to update ${name}/${id}`)}
      return snakeToCamel<T>(await res.json())
    },

    async delete(id: string): Promise<boolean> {
      const res = await apiFetch(`${basePath}${id}/`, { method: 'DELETE' })
      return res.status === 204 || res.ok
    },

    filter(): string {
      return ''
    },

    async getFirstListItem(): Promise<T> {
      const params = new URLSearchParams()
      params.set('page_size', '1')
      const res = await apiFetch(`${basePath}?${params.toString()}`)
      if (!res.ok) {throw new Error(`Failed to get first ${name}`)}
      const data: ApiListResponse<Record<string, unknown>> = await res.json()
      if (!data.results.length) {
        const err = new Error('The requested resource was not found.') as Error & { status: number }
        err.status = 404
        throw err
      }
      return snakeToCamel<T>(data.results[0])
    },

  }
}

export const api = {
  collection: createCollection,
}
